/**
 * Issues Service
 *
 * Data access layer for issue tracking, comments, and photos.
 * Handles issue lifecycle (reported → confirmed → repair_assigned → resolved → closed).
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { compressImage } from '../lib/storage';
import { fetchAllPages, dedupeById } from '../lib/fetchAllPages';

// ============================================================================
// Types
// ============================================================================

export interface Issue {
  id: string;
  merchant_id: string;
  location_id: string | null;
  inventory_item_id: string | null;
  product_type_id: string | null;
  booking_id: string | null;
  booking_item_return_id: string | null;
  title: string;
  description: string | null;
  source: 'internal' | 'customer_return' | 'system';
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string | null;
  status: 'reported' | 'confirmed' | 'dismissed' | 'repair_assigned' | 'resolved' | 'cannot_repair' | 'closed';
  blocking_time_id: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
  confirmation_notes: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  assignee_type: string | null;
  assignee_name: string | null;
  assigned_to_user_id?: string | null;
  repair_notes: string | null;
  repair_due_date: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  resolution_type: string | null;
  reported_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface IssueWithRelations extends Issue {
  locations?: { location_name: string } | null;
  inventory_items?: { item_number: string; nr: number; product_type_id: string; size: string | null } | null;
  product_types?: { product_name: string; product_type: string; location_id: string } | null;
  bookings?: { order_id: string; customer_firstname: string; customer_lastname: string } | null;
  reported_by_profile?: { full_name: string } | null;
  confirmed_by_profile?: { full_name: string } | null;
  assigned_by_profile?: { full_name: string } | null;
  resolved_by_profile?: { full_name: string } | null;
  issue_photos?: IssuePhoto[];
  issue_comments?: IssueComment[];
}

export interface IssueComment {
  id: string;
  issue_id: string;
  user_id: string | null;
  comment: string;
  is_system: boolean;
  created_at: string;
  profiles?: { full_name: string } | null;
}

export interface IssuePhoto {
  id: string;
  issue_id: string;
  merchant_id: string;
  uploaded_by: string | null;
  file_name: string;
  file_path: string;
  created_at: string;
}

export interface IssueInsert {
  merchant_id: string;
  location_id?: string | null;
  inventory_item_id?: string | null;
  product_type_id?: string | null;
  booking_id?: string | null;
  booking_item_return_id?: string | null;
  title: string;
  description?: string | null;
  source?: 'internal' | 'customer_return' | 'system';
  severity?: 'low' | 'medium' | 'high' | 'critical';
  category?: string | null;
  status?: string;
  repair_due_date?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  reported_by?: string | null;
}

export interface IssueFilters {
  status?: string[];
  severity?: string[];
  locationId?: string;
  source?: string;
  productTypeId?: string;
  assigned?: 'assigned' | 'unassigned';
  assigneeUserId?: string;
  assigneeName?: string;
  searchQuery?: string;
}

export interface IssueStats {
  total: number;
  reported: number;
  confirmed: number;
  repairAssigned: number;
  overdue: number;
}

// ============================================================================
// Issue CRUD
// ============================================================================

const ISSUE_SELECT = `
  *,
  locations(location_name),
  inventory_items(item_number, nr, product_type_id, size),
  product_types(product_name, product_type, location_id),
  bookings(order_id, customer_firstname, customer_lastname),
  reported_by_profile:profiles!issues_reported_by_fkey(full_name),
  confirmed_by_profile:profiles!issues_confirmed_by_fkey(full_name),
  assigned_by_profile:profiles!issues_assigned_by_fkey(full_name),
  resolved_by_profile:profiles!issues_resolved_by_fkey(full_name)
`;

/**
 * Get issues with optional filters, sorted by created_at desc (newest first)
 */
export async function getIssues(
  merchantId: string,
  filters?: IssueFilters
) {
  try {
    // Paged: the merchant-wide issue list grows without bound, and a single
    // unbounded request stops at the PostgREST row cap with no error — the
    // oldest issues would just stop appearing, and the page's client-side
    // search would only ever search the surviving page. `id` is a unique
    // tie-breaker so rows sharing a `created_at` cannot straddle a page edge.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('issues')
        .select(ISSUE_SELECT)
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }

      if (filters?.severity && filters.severity.length > 0) {
        query = query.in('severity', filters.severity);
      }

      if (filters?.locationId) {
        query = query.eq('location_id', filters.locationId);
      }

      if (filters?.source) {
        query = query.eq('source', filters.source);
      }

      if (filters?.productTypeId) {
        query = query.eq('product_type_id', filters.productTypeId);
      }

      if (filters?.assigned === 'assigned') {
        query = query.not('assignee_name', 'is', null);
      } else if (filters?.assigned === 'unassigned') {
        query = query.is('assignee_name', null);
      }

      if (filters?.assigneeUserId) {
        query = query.eq('assigned_to_user_id', filters.assigneeUserId);
      }

      if (filters?.assigneeName) {
        query = query.eq('assignee_name', filters.assigneeName);
      }

      if (filters?.searchQuery) {
        // Escape characters that have special meaning in PostgREST filter syntax
        const escaped = filters.searchQuery
          .replace(/\\/g, '\\\\')
          .replace(/%/g, '\\%')
          .replace(/,/g, '\\,')
          .replace(/\(/g, '\\(')
          .replace(/\)/g, '\\)')
          .replace(/\./g, '\\.');
        query = query.or(
          `title.ilike.%${escaped}%,assignee_name.ilike.%${escaped}%`
        );
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    return { data: data as IssueWithRelations[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get issues for a specific inventory item
 */
export async function getIssuesByInventoryItem(inventoryItemId: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .select(ISSUE_SELECT)
      .eq('inventory_item_id', inventoryItemId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data as IssueWithRelations[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get a single issue with all relations
 */
export async function getIssueById(issueId: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .select(`
        ${ISSUE_SELECT},
        issue_photos(*),
        issue_comments(*, profiles(full_name))
      `)
      .eq('id', issueId)
      .order('created_at', { ascending: true, referencedTable: 'issue_comments' })
      .single();

    if (error) throw error;
    return { data: data as unknown as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new issue
 */
export async function createIssue(issue: IssueInsert) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .insert(issue)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an issue (general fields)
 */
export async function updateIssue(issueId: string, updates: Partial<Issue>) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

// ============================================================================
// Status transitions
// ============================================================================

/**
 * Confirm an issue (reported → confirmed)
 */
export async function confirmIssue(issueId: string, userId: string, notes?: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .update({
        status: 'confirmed',
        confirmed_at: new Date().toISOString(),
        confirmed_by: userId,
        confirmation_notes: notes || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;

    // Add system comment
    await addSystemComment(issueId, `Status changed to confirmed`, userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Dismiss an issue (reported → dismissed → closed)
 */
export async function dismissIssue(issueId: string, userId: string, notes?: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .update({
        status: 'dismissed',
        resolved_at: new Date().toISOString(),
        resolved_by: userId,
        resolution_notes: notes || null,
        resolution_type: 'no_action_needed',
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(issueId, `Issue dismissed${notes ? `: ${notes}` : ''}`, userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Assign repair
 */
export async function assignRepair(
  issueId: string,
  userId: string,
  assignment: {
    assignee_type: 'internal' | 'external';
    assignee_name: string;
    assigned_to_user_id?: string | null;
    repair_notes?: string;
    repair_due_date?: string;
  }
) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .update({
        status: 'repair_assigned',
        assigned_at: new Date().toISOString(),
        assigned_by: userId,
        assignee_type: assignment.assignee_type,
        assignee_name: assignment.assignee_name,
        assigned_to_user_id: assignment.assigned_to_user_id || null,
        repair_notes: assignment.repair_notes || null,
        // Only overwrite repair_due_date if explicitly provided, to preserve
        // any due date that was set earlier (e.g. during issue creation)
        ...(assignment.repair_due_date !== undefined && {
          repair_due_date: assignment.repair_due_date || null,
        }),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(
      issueId,
      `Repair assigned to ${assignment.assignee_name} (${assignment.assignee_type})${assignment.repair_due_date ? `, due ${assignment.repair_due_date}` : ''}`,
      userId
    );

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Resolve an issue, optionally resetting the maintenance check counter
 */
export async function resolveIssue(
  issueId: string,
  userId: string,
  resolution: {
    resolution_notes?: string;
    resolution_type: 'repaired' | 'replaced_part' | 'retired_item' | 'no_action_needed' | 'cannot_repair';
    reset_check_counter?: boolean;
  }
) {
  try {
    // Get the issue to find inventory_item_id
    const { data: issue, error: fetchError } = await supabase
      .from('issues')
      .select('inventory_item_id, blocking_time_id')
      .eq('id', issueId)
      .single();

    if (fetchError) throw fetchError;

    // Update issue status — always use 'resolved' (cannot_repair is now a resolution type, not a status)
    const { data, error } = await supabase
      .from('issues')
      .update({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolved_by: userId,
        resolution_notes: resolution.resolution_notes || null,
        resolution_type: resolution.resolution_type,
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;

    // If reset_check_counter and there's an inventory item, reset the tracking fields
    if (resolution.reset_check_counter && issue?.inventory_item_id) {
      const { data: item } = await supabase
        .from('inventory_items')
        .select('usage_hours, usage_times')
        .eq('id', issue.inventory_item_id)
        .single();

      if (item) {
        await supabase
          .from('inventory_items')
          .update({
            last_check_usage_hours: item.usage_hours || 0,
            last_check_usage_times: item.usage_times || 0,
          })
          .eq('id', issue.inventory_item_id);
      }
    }

    // End blocking time if one exists
    if (issue?.blocking_time_id) {
      await supabase
        .from('inventory_blocking_times')
        .update({
          blocked_until: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', issue.blocking_time_id);
    }

    await addSystemComment(issueId, `Issue resolved: ${resolution.resolution_type}${resolution.reset_check_counter ? ' (maintenance check counter reset)' : ''}`, userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Mark as cannot repair
 */
export async function markCannotRepair(issueId: string, userId: string, notes?: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .update({
        status: 'cannot_repair',
        resolved_at: new Date().toISOString(),
        resolved_by: userId,
        resolution_notes: notes || null,
        resolution_type: 'retired_item',
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(issueId, `Marked as cannot repair${notes ? `: ${notes}` : ''}`, userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Close an issue (final state after resolved/dismissed)
 */
export async function closeIssue(issueId: string, userId?: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .update({
        status: 'closed',
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(issueId, 'Issue closed', userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Reopen an issue (backward transition).
 * resolved → repair_assigned (if was assigned) or confirmed
 * closed → resolved
 * dismissed → reported
 * Does NOT re-block the item — user can re-block manually if needed.
 */
export async function reopenIssue(issueId: string, userId: string) {
  try {
    const { data: issue, error: fetchError } = await supabase
      .from('issues')
      .select('status, assignee_name')
      .eq('id', issueId)
      .single();

    if (fetchError) throw fetchError;

    let targetStatus: string;
    const clearResolution: Record<string, null> = {};

    switch (issue.status) {
      case 'resolved':
      case 'cannot_repair':
        targetStatus = issue.assignee_name ? 'repair_assigned' : 'confirmed';
        clearResolution.resolved_at = null;
        clearResolution.resolved_by = null;
        clearResolution.resolution_notes = null;
        clearResolution.resolution_type = null;
        break;
      case 'closed':
        targetStatus = 'resolved';
        break;
      case 'dismissed':
        targetStatus = 'reported';
        clearResolution.resolved_at = null;
        clearResolution.resolved_by = null;
        clearResolution.resolution_notes = null;
        clearResolution.resolution_type = null;
        break;
      default:
        return { data: null, error: `Cannot reopen an issue with status "${issue.status}"` };
    }

    const { data, error } = await supabase
      .from('issues')
      .update({
        status: targetStatus,
        ...clearResolution,
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(issueId, `Issue reopened → ${targetStatus.replace(/_/g, ' ')}`, userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

// ============================================================================
// Blocking
// ============================================================================

/**
 * Block an inventory item for this issue (manual action)
 */
export async function blockItemForIssue(
  issueId: string,
  inventoryItemId: string,
  userId: string,
  blockedUntil?: string
) {
  try {
    const now = new Date();
    // Ensure blocked_until is always in the future (after blocked_from)
    // to satisfy the check_blocking_dates constraint
    const defaultUntil = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
    let until = defaultUntil;
    if (blockedUntil && new Date(blockedUntil) > now) {
      until = blockedUntil;
    }

    // Create blocking time
    const { data: blocking, error: blockError } = await supabase
      .from('inventory_blocking_times')
      .insert({
        inventory_item_id: inventoryItemId,
        blocked_from: now.toISOString(),
        blocked_until: until,
        blocking_reason: 'repair',
        notes: `Blocked for issue repair`,
        created_by_user_id: userId,
      })
      .select()
      .single();

    if (blockError) throw blockError;

    // Link blocking time to issue
    const { data, error } = await supabase
      .from('issues')
      .update({
        blocking_time_id: blocking.id,
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(issueId, 'Item blocked for repair', userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Unblock an inventory item for this issue
 */
export async function unblockItemForIssue(issueId: string, blockingTimeId: string, userId?: string) {
  try {
    // End the blocking period
    await supabase
      .from('inventory_blocking_times')
      .update({
        blocked_until: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', blockingTimeId);

    // Unlink from issue
    const { data, error } = await supabase
      .from('issues')
      .update({
        blocking_time_id: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId)
      .select(ISSUE_SELECT)
      .single();

    if (error) throw error;
    await addSystemComment(issueId, 'Item unblocked', userId);

    return { data: data as IssueWithRelations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

// ============================================================================
// Comments
// ============================================================================

/**
 * Add a user comment to an issue
 */
export async function addComment(issueId: string, userId: string, comment: string) {
  try {
    const { data, error } = await supabase
      .from('issue_comments')
      .insert({
        issue_id: issueId,
        user_id: userId,
        comment,
        is_system: false,
      })
      .select('*, profiles(full_name)')
      .single();

    if (error) throw error;
    return { data: data as IssueComment, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Add a system comment (for audit trail)
 */
async function addSystemComment(issueId: string, comment: string, userId?: string) {
  await supabase
    .from('issue_comments')
    .insert({
      issue_id: issueId,
      user_id: userId || null,
      comment,
      is_system: true,
    });
}

/**
 * Get comments for an issue
 */
export async function getIssueComments(issueId: string) {
  try {
    const { data, error } = await supabase
      .from('issue_comments')
      .select('*, profiles(full_name)')
      .eq('issue_id', issueId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return { data: data as IssueComment[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

// ============================================================================
// Photos
// ============================================================================

const STORAGE_BUCKET = 'products';
const ISSUE_PHOTOS_PATH = 'product-images';

/**
 * Upload a photo for an issue
 */
export async function uploadIssuePhoto(
  issueId: string,
  merchantId: string,
  userId: string,
  file: File
) {
  try {
    if (!file.type.startsWith('image/')) {
      throw new Error('Please upload an image file');
    }
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File size must be less than 10MB');
    }

    // Compress image
    const compressed = await compressImage(file);

    const fileExt = compressed.name.split('.').pop();
    const filePath = `${ISSUE_PHOTOS_PATH}/${merchantId}/issues/${issueId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, compressed, { cacheControl: '3600', upsert: false });

    if (uploadError) throw uploadError;

    // Save record
    const { data, error } = await supabase
      .from('issue_photos')
      .insert({
        issue_id: issueId,
        merchant_id: merchantId,
        uploaded_by: userId,
        file_name: file.name,
        file_path: filePath,
      })
      .select()
      .single();

    if (error) throw error;

    // Add activity entry
    await addSystemComment(issueId, `Photo added: ${file.name}`, userId);

    return { data: data as IssuePhoto, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get public URL for an issue photo
 */
export function getIssuePhotoUrl(filePath: string): string {
  return supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(filePath).data.publicUrl;
}

/**
 * Delete an issue photo
 */
export async function deleteIssuePhoto(photoId: string, filePath: string) {
  try {
    await supabase.storage.from(STORAGE_BUCKET).remove([filePath]);

    const { error } = await supabase
      .from('issue_photos')
      .delete()
      .eq('id', photoId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

// ============================================================================
// Affected bookings
// ============================================================================

/**
 * Resolve the upper bound of the "affected bookings" window.
 *
 * A date-only value (e.g. the issues.repair_due_date column, `YYYY-MM-DD`)
 * would otherwise be compared as midnight UTC of that day, which drops every
 * booking that starts later on the same day. Treat it as the end of that day.
 * Full timestamps (e.g. inventory_blocking_times.blocked_until) pass through.
 */
export function resolveAffectedBookingsUntil(untilDate?: string | null): string {
  if (!untilDate) {
    return new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(untilDate)) {
    return new Date(untilDate + 'T23:59:59.999').toISOString();
  }
  return new Date(untilDate).toISOString();
}

/**
 * Get upcoming bookings that use a specific inventory item.
 * Used to show affected bookings when considering blocking.
 *
 * `untilDate` is the end of the unavailability window: the blocking period's
 * blocked_until when the item is blocked, otherwise the repair due date.
 * Without either, the next 14 days are shown.
 */
export async function getAffectedBookings(
  inventoryItemId: string,
  untilDate?: string | null
) {
  try {
    const now = new Date().toISOString();
    const until = resolveAffectedBookingsUntil(untilDate);

    const { data, error } = await supabase
      .from('booking_items')
      .select(`
        id,
        inventory_item_id,
        item_name,
        bookings!inner(
          id, order_id, customer_firstname, customer_lastname,
          start_date, end_date, booking_state
        )
      `)
      .eq('inventory_item_id', inventoryItemId)
      .in('bookings.booking_state', ['open', 'active'])
      .gte('bookings.end_date', now)
      .lte('bookings.start_date', until);

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

// ============================================================================
// Dashboard stats
// ============================================================================

/**
 * Get issue statistics for dashboard widget
 */
export async function getIssueStats(merchantId: string): Promise<{ data: IssueStats | null; error: string | null }> {
  try {
    // Paged for the same reason as getIssues: these counters are totals over
    // every open issue, so a truncated fetch would understate the dashboard
    // widget. `id` is selected purely as the paging sort and dedupe key.
    const data = dedupeById(
      await fetchAllPages<any>((from, to) =>
        supabase
          .from('issues')
          .select('id, status, repair_due_date')
          .eq('merchant_id', merchantId)
          .in('status', ['reported', 'confirmed', 'repair_assigned'])
          .order('id', { ascending: true })
          .range(from, to)
      )
    );

    const today = new Date().toISOString().split('T')[0];
    const stats: IssueStats = {
      total: data?.length || 0,
      reported: data?.filter((i: any) => i.status === 'reported').length || 0,
      confirmed: data?.filter((i: any) => i.status === 'confirmed').length || 0,
      repairAssigned: data?.filter((i: any) => i.status === 'repair_assigned').length || 0,
      overdue: data?.filter((i: any) => i.repair_due_date && i.repair_due_date < today).length || 0,
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Check if an issue already exists for a booking item return
 */
export async function getIssueByBookingItemReturn(bookingItemReturnId: string) {
  try {
    const { data, error } = await supabase
      .from('issues')
      .select('id, title, status')
      .eq('booking_item_return_id', bookingItemReturnId)
      .maybeSingle();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get the last completed booking for an inventory item (for linking from maintenance tab)
 */
export async function getLastBookingForItem(inventoryItemId: string) {
  try {
    const { data, error } = await supabase
      .from('booking_items')
      .select(`
        bookings!inner(
          id, order_id, customer_firstname, customer_lastname,
          end_date, booking_state
        )
      `)
      .eq('inventory_item_id', inventoryItemId)
      .eq('bookings.booking_state', 'returned')
      .order('bookings(end_date)', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return { data: data?.bookings || null, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
